import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getEmployer } from '../apiconfig/api';
import { useSession } from './SessionContext';

export interface EmployerData {
  uuid: string;
  companyName: string;
  slug: string;
  companyAddress: string;
  about: string;
  introVideoUuid?: string;
  // Add other employer fields as needed
}

export interface EmployerContextType {
  employer: EmployerData | null;
  loading: boolean;
  error: string | null;
  refreshEmployer: () => Promise<void>;
  updateEmployer: (updates: Partial<EmployerData>) => void;
}

const EmployerContext = createContext<EmployerContextType | undefined>(undefined);

export const EmployerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { sessionEmployerId } = useSession();
  const [employer, setEmployer] = useState<EmployerData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchEmployer = async () => {
    if (!sessionEmployerId) {
      setEmployer(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await getEmployer(sessionEmployerId);
      setEmployer(response.data.employer);
    } catch (err: any) {
      setError(err.message || 'Failed to load employer data');
      setEmployer(null);
    } finally {
      setLoading(false);
    }
  };

  const refreshEmployer = async () => {
    await fetchEmployer();
  };

  const updateEmployer = (updates: Partial<EmployerData>) => {
    if (employer) {
      setEmployer({ ...employer, ...updates });
    }
  };

  // Fetch employer data when sessionEmployerId changes
  useEffect(() => {
    fetchEmployer();
  }, [sessionEmployerId]);

  const value: EmployerContextType = {
    employer,
    loading,
    error,
    refreshEmployer,
    updateEmployer,
  };

  return (
    <EmployerContext.Provider value={value}>
      {children}
    </EmployerContext.Provider>
  );
};

export const useEmployer = () => {
  const context = useContext(EmployerContext);
  if (context === undefined) {
    throw new Error('useEmployer must be used within an EmployerProvider');
  }
  return context;
}; 